import type { SeverityLevel } from '@sentry/core'
import { getSentry } from '~/utility/lazySentry'

interface ExceptionLike {
  stack?: string
  message?: string
  issues?: unknown[]
}

/**
 * Determines if the value is an actual Error instance or exception-like object
 */
function isException(error: unknown): error is Error | ExceptionLike {
  return (
    error instanceof Error ||
    (error !== null && typeof error === 'object' && 'stack' in error) ||
    (error !== null &&
      typeof error === 'object' &&
      'message' in error &&
      typeof (error as ExceptionLike).message === 'string')
  )
}

/**
 * Sentry error capture utility — uses dynamic import to keep Sentry out of the main bundle.
 * Routes to captureException for Error objects or captureMessage for strings/events.
 * Callers fire-and-forget (don't await), so the async change is safe.
 */
export default async function sentryCapture(error: unknown) {
  try {
    const { captureException, captureMessage } = await getSentry()
    if (isException(error)) {
      captureException(error)
    } else {
      // For non-exception errors (strings, plain objects, events)
      const message = typeof error === 'string' ? error : JSON.stringify(error)
      captureMessage(message, 'error')
    }
  } catch (sentryError) {
    // Fallback to console if anything fails
    console.error('Sentry capture failed:', sentryError)
    console.error('Original error:', error)
    const err = error as ExceptionLike | null
    if (err?.message) {
      console.error('Error message:', err.message)
    }
    if (err?.issues) {
      err.issues.forEach((issue: unknown) => {
        console.error('Issue:', issue)
      })
    }
  }
}

/**
 * Captures messages with Sentry or falls back to console logging
 */
export async function sentryCaptureMessage(
  message: string,
  level?: SeverityLevel,
) {
  try {
    const { captureMessage } = await getSentry()
    captureMessage(message, level)
  } catch (sentryError) {
    // Fallback to console if anything fails
    console.error('Sentry message capture failed:', sentryError)
    console.error('Original message:', message)
  }
}
